import { Quote } from '../../../mocks/quotes';

interface QuoteAnalyticsProps {
  quotes: Quote[];
}

const AGENT_COLORS: Record<string, string> = {
  'Mohammed Khalid': 'bg-amber-500',
  'Sara Ahmed': 'bg-teal-500',
  'Fatima Al-Zahra': 'bg-violet-500',
};

const AGENT_TEXT: Record<string, string> = {
  'Mohammed Khalid': 'text-amber-600',
  'Sara Ahmed': 'text-teal-600',
  'Fatima Al-Zahra': 'text-violet-600',
};

export default function QuoteAnalytics({ quotes }: QuoteAnalyticsProps) {
  const total = quotes.length;
  const sent = quotes.filter((q) => ['Sent', 'Accepted', 'Rejected', 'Expired'].includes(q.status)).length;
  const accepted = quotes.filter((q) => q.status === 'Accepted').length;
  const rejected = quotes.filter((q) => q.status === 'Rejected').length;
  const expired = quotes.filter((q) => q.status === 'Expired').length;
  const pending = quotes.filter((q) => q.status === 'Sent').length;
  const acceptRate = sent > 0 ? Math.round((accepted / sent) * 100) : 0;

  const totalValue = quotes.reduce((s, q) => s + q.totalAmount, 0);
  const acceptedValue = quotes.filter((q) => q.status === 'Accepted').reduce((s, q) => s + q.totalAmount, 0);

  // Agent breakdown
  const agents = ['Mohammed Khalid', 'Sara Ahmed', 'Fatima Al-Zahra'];
  const agentStats = agents.map((agent) => {
    const agentQuotes = quotes.filter((q) => q.agent === agent);
    const agentAccepted = agentQuotes.filter((q) => q.status === 'Accepted');
    const agentValue = agentQuotes.reduce((s, q) => s + q.totalAmount, 0);
    const agentWinRate = agentQuotes.length > 0 ? Math.round((agentAccepted.length / agentQuotes.length) * 100) : 0;
    return { agent, count: agentQuotes.length, accepted: agentAccepted.length, value: agentValue, winRate: agentWinRate };
  });

  // Destination breakdown
  const destMap: Record<string, { count: number; accepted: number; value: number }> = {};
  quotes.forEach((q) => {
    const dest = q.destination.split(' ')[0];
    if (!destMap[dest]) destMap[dest] = { count: 0, accepted: 0, value: 0 };
    destMap[dest].count++;
    if (q.status === 'Accepted') destMap[dest].accepted++;
    destMap[dest].value += q.totalAmount;
  });
  const destinations = Object.entries(destMap)
    .sort((a, b) => b[1].value - a[1].value)
    .slice(0, 5);

  const maxDestValue = Math.max(...destinations.map(([, d]) => d.value), 1);

  return (
    <div className="space-y-6">
      {/* Funnel */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-5">Quote Conversion Funnel</h3>
        <div className="space-y-3">
          {[
            { label: 'Quotes Created', value: total, color: 'bg-gray-200', pct: 100 },
            { label: 'Quotes Sent', value: sent, color: 'bg-amber-300', pct: total > 0 ? (sent / total) * 100 : 0 },
            { label: 'Pending Response', value: pending, color: 'bg-sky-300', pct: total > 0 ? (pending / total) * 100 : 0 },
            { label: 'Accepted', value: accepted, color: 'bg-emerald-400', pct: total > 0 ? (accepted / total) * 100 : 0 },
            { label: 'Rejected', value: rejected, color: 'bg-red-300', pct: total > 0 ? (rejected / total) * 100 : 0 },
          ].map((row, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-36 text-xs text-gray-500 text-right flex-shrink-0">{row.label}</div>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div
                  className={`h-full ${row.color} rounded-full flex items-center justify-end pr-3 transition-all duration-500`}
                  style={{ width: `${Math.max(row.pct, 4)}%` }}
                >
                  <span className="text-xs font-bold text-white">{row.value}</span>
                </div>
              </div>
              <div className="w-12 text-xs text-gray-400 flex-shrink-0">{row.pct.toFixed(0)}%</div>
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-emerald-600">{acceptRate}%</p>
            <p className="text-xs text-gray-500 mt-0.5">Win Rate</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-amber-600">
              AED {(totalValue / (total || 1)).toLocaleString('en-AE', { maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Avg Quote Value</p>
          </div>
          <div className="bg-teal-50 rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-teal-600">
              AED {acceptedValue.toLocaleString('en-AE', { maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Revenue Won</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Agent Performance */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Agent Performance</h3>
          <div className="space-y-4">
            {agentStats.map((a) => (
              <div key={a.agent} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${AGENT_COLORS[a.agent] || 'bg-gray-400'}`}>
                      {a.agent.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{a.agent.split(' ')[0]}</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${AGENT_TEXT[a.agent] || 'text-gray-600'}`}>{a.winRate}%</span>
                    <span className="text-xs text-gray-400 ml-1">win rate</span>
                  </div>
                </div>
                <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${AGENT_COLORS[a.agent] || 'bg-gray-400'} rounded-full transition-all duration-500`}
                    style={{ width: `${a.winRate}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>{a.count} quotes · {a.accepted} accepted</span>
                  <span>AED {a.value.toLocaleString('en-AE', { maximumFractionDigits: 0 })}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Destination Value */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Destinations by Value</h3>
          <div className="space-y-3">
            {destinations.map(([dest, data], i) => (
              <div key={dest} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="font-medium text-gray-700">{dest}</span>
                  </div>
                  <span className="font-semibold text-gray-800">
                    AED {data.value.toLocaleString('en-AE', { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-500"
                    style={{ width: `${(data.value / maxDestValue) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>{data.count} quotes</span>
                  <span className="text-emerald-600 font-medium">{data.accepted} accepted</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly Volume (mock chart) */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Monthly Quote Volume &amp; Win Rate</h3>
        <div className="flex items-end gap-3 h-36">
          {[
            { month: 'Oct', total: 5, accepted: 3 },
            { month: 'Nov', total: 7, accepted: 4 },
            { month: 'Dec', total: 9, accepted: 6 },
            { month: 'Jan', total: 11, accepted: 7 },
            { month: 'Feb', total: 8, accepted: 5 },
            { month: 'Mar', total: quotes.length, accepted },
          ].map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col items-center gap-0.5 flex-1 justify-end">
                <div
                  className="w-full bg-amber-400 rounded-t-lg transition-all duration-500"
                  style={{ height: `${(m.total / 12) * 100}%` }}
                />
                <div
                  className="w-full bg-emerald-400 rounded-t-lg transition-all duration-500"
                  style={{ height: `${(m.accepted / 12) * 100}%`, marginTop: '-100%', opacity: 0.6 }}
                />
              </div>
              <span className="text-xs text-gray-400">{m.month}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> Total Quotes
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-3 h-3 rounded-sm bg-emerald-400 inline-block" /> Accepted
          </div>
        </div>
      </div>
    </div>
  );
}
